import {
  Loader,
  SignalViewerManagedLicenseProvider,
  useSignalViewerLicense,
} from '@lmt/signal-viewer-ui';

import { env } from './config/env';
import { useReadQueryParamsFromLocation } from './utils/query-helper';
import { ChartsPage } from './pages/ChartPage';
import { NotificationProvider } from './components/notifications/NotificationProvider';

export type ViewerMode = 'report' | 'interactive' | 'standalone';

// Branding for the LightningChart license used by this app. Trial values while on a trial key;
// update these when moving to an app-deployment license (must match the LC Customer Portal).
const LICENSE_INFORMATION = {
  appTitle: 'LightningChart JS Trial',
  company: 'LightningChart Ltd.',
};

// Splash over the page while the license loads at startup. Skipped in report mode: the report
// PDF is rendered by Puppeteer against this same SPA, and the splash must not end up in a PDF.
const StartupOverlay: React.FC<{ mode: ViewerMode }> = ({ mode }) => {
  const { loading } = useSignalViewerLicense();
  if (mode === 'report' || !loading) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#fff',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Loader />
    </div>
  );
};

export const App: React.FC = () => {
  const { reportMode, separateWindow } = useReadQueryParamsFromLocation();

  const viewerMode: ViewerMode = reportMode
    ? 'report'
    : separateWindow
      ? 'standalone'
      : 'interactive';

  return (
    <SignalViewerManagedLicenseProvider
      clientKey={env.licenseClientKey}
      apiKey={env.apiKey}
      backendUrl={env.baseUrl}
      licenseInformation={LICENSE_INFORMATION}
    >
      <NotificationProvider>
        <StartupOverlay mode={viewerMode} />
        <ChartsPage mode={viewerMode} />
      </NotificationProvider>
    </SignalViewerManagedLicenseProvider>
  );
};
